import type { PreviewTeamSettingsChangesResult } from "@redotech/admin-sdk/rpc/schema/settings-approval/preview-settings-changes";
import ShieldTickIcon from "@redotech/redo-icons/shield-tick.svg";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import type { ProtectedSettingsAction } from "../../client/team";
import * as styles from "./settings-approval-modal.module.css";

interface AffectedSetting {
  label: string;
  hasPending: boolean;
}

/**
 * Shared modal body that renders the "Affected settings" list with warning
 * dots and optional "Updates pending" badges. Used by both the admin logistics
 * page (multi-setting preview) and the merchant integration detail page
 * (single-setting).
 */
export const SettingsApprovalContent = memo(function SettingsApprovalContent({
  settings,
}: {
  settings: readonly AffectedSetting[];
}) {
  return (
    <Flex dir="column" gap="sm">
      <Text fontSize="xxs" fontWeight="semibold" textColor="tertiary">
        AFFECTED SETTINGS
      </Text>
      <Flex className={styles.settingsList} dir="column" gap="none">
        {settings.map((setting, i) => (
          <Flex
            align="center"
            className={i < settings.length - 1 ? styles.settingsRow : undefined}
            gap="sm"
            justify="space-between"
            key={setting.label}
            px="lg"
            py="md"
          >
            <Flex align="center" gap="sm">
              <div className={styles.warningDot} />
              <Text fontSize="sm" fontWeight="medium">
                {setting.label}
              </Text>
            </Flex>
            {setting.hasPending && (
              <RedoBadge color="warning" size="xs" text="Updates pending" />
            )}
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
});

export const SettingsApprovalModal = memo(function SettingsApprovalModal({
  preview,
  pending,
  onChoose,
  onClose,
}: {
  preview: PreviewTeamSettingsChangesResult;
  pending: boolean;
  onChoose: (mode: ProtectedSettingsAction) => void;
  onClose: () => void;
}) {
  const { nonProtectedCount, canBypassAll, existingPending } = preview;
  const protectedChanges = preview.protected;

  const hasExisting = existingPending.length > 0;

  const affectedSettings = useMemo(() => {
    const existingPaths = new Set(existingPending.map((p) => p.path));
    const seen = new Map<string, boolean>();
    for (const change of protectedChanges) {
      const label = change.label ?? change.path;
      if (!seen.has(label)) {
        seen.set(label, existingPaths.has(change.path));
      } else if (existingPaths.has(change.path)) {
        seen.set(label, true);
      }
    }
    return [...seen.entries()].map(([label, hasPending]) => ({
      label,
      hasPending,
    }));
  }, [protectedChanges, existingPending]);

  const title = hasExisting
    ? "Add to the pending approval request"
    : `${affectedSettings.length} setting${affectedSettings.length === 1 ? "" : "s"} require approval`;

  const subtitle = hasExisting
    ? "There's already a request awaiting review. Your change will be added to it."
    : nonProtectedCount > 0
      ? `${nonProtectedCount} other change${nonProtectedCount === 1 ? "" : "s"} will be saved immediately.`
      : "These changes need to be reviewed before they take effect.";

  const primaryText = hasExisting ? "Append to request" : "Request approval";

  return (
    <RedoModal
      additionalFooterContent={
        canBypassAll ? (
          <BypassLink
            onClick={() => onChoose("bypassApproval")}
            pending={pending}
          />
        ) : undefined
      }
      buttonPlacement="full"
      footerLayout="column"
      isOpen
      modalSize="md"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: primaryText,
        onClick: () => onChoose("requestApproval"),
        disabled: pending,
        pending,
      }}
      secondaryButton={{ text: "Discard", onClick: onClose, disabled: pending }}
      subtitle={subtitle}
      theme="warn"
      title={title}
      TitleIcon={ShieldTickIcon}
    >
      <SettingsApprovalContent settings={affectedSettings} />
    </RedoModal>
  );
});

export const BypassLink = memo(function BypassLink({
  onClick,
  pending,
}: {
  onClick: () => void;
  pending: boolean;
}) {
  return (
    <Flex align="center" justify="center" pt="lg" style={{ order: 1 }} w="full">
      <Text
        fontSize="xs"
        onClick={pending ? undefined : onClick}
        style={{
          textDecoration: "underline",
          cursor: pending ? "default" : "pointer",
          opacity: pending ? 0.5 : 1,
        }}
        textColor="tertiary"
      >
        Apply now without approval
      </Text>
    </Flex>
  );
});
